import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, GripVertical, Check, X } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card/Card';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Input } from '@/components/ui/Input/Input';
import { Switch } from '@/components/ui/Switch/Switch';
import { Modal } from '@/components/ui/Modal/Modal';
import { Select } from '@/components/ui/Select/Select';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { TipTapEditor } from '@/components/admin/TipTapEditor';
import type { BillingPlanDto, BillingPlanFeatureDto } from '@/services/BillingService';
import {
  AdminBillingService,
  type AdminFeatureInput,
  type AdminFeatureUpdateInput,
} from '@/services/AdminBillingService';
import { useNotificationStore } from '@/stores/notificationStore';

const CATEGORY_OPTIONS = [
  { value: '__none__', label: 'No category' },
  { value: 'generation', label: 'Generation' },
  { value: 'platform', label: 'Platform' },
  { value: 'collaboration', label: 'Collaboration' },
  { value: 'support', label: 'Support' },
];

const categoryBadge = (cat: string | null) => {
  if (!cat) return null;
  const colors: Record<string, 'default' | 'secondary' | 'success' | 'warning'> = {
    generation: 'default',
    platform: 'secondary',
    collaboration: 'success',
    support: 'warning',
  };
  return <Badge variant={colors[cat] ?? 'secondary'}>{cat}</Badge>;
};

type FeatureModalState =
  | { mode: 'create'; plan: BillingPlanDto }
  | { mode: 'edit'; plan: BillingPlanDto; feature: BillingPlanFeatureDto };

interface FeatureFormState {
  featureText: string;
  isIncluded: boolean;
  category: string;
  tooltipHtml: string;
}

const emptyForm: FeatureFormState = {
  featureText: '',
  isIncluded: true,
  category: '__none__',
  tooltipHtml: '',
};

export function PlanFeaturesAdmin() {
  const [searchParams] = useSearchParams();
  const queryPlanId = searchParams.get('planId');

  const service = React.useMemo(() => new AdminBillingService(), []);
  const [plans, setPlans] = React.useState<BillingPlanDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = React.useState<string>('');
  const [featureModal, setFeatureModal] = React.useState<FeatureModalState | null>(null);
  const [form, setForm] = React.useState<FeatureFormState>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [modalError, setModalError] = React.useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<{ feature: BillingPlanFeatureDto; plan: BillingPlanDto } | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const [dropLinePos, setDropLinePos] = React.useState<number | null>(null); // insertion index (0..length)
  const [reordering, setReordering] = React.useState(false);

  const sortPlans = (list: BillingPlanDto[]) =>
    [...list].sort((a, b) => a.displayOrder - b.displayOrder);

  const loadPlans = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await service.listPlans();
      const sorted = sortPlans(data);
      setPlans(sorted);
      if (!selectedPlanId && sorted.length > 0) {
        // Use planId from URL query param if present, otherwise first plan
        const initialId = queryPlanId && sorted.some((p) => p.id === queryPlanId)
          ? queryPlanId
          : sorted[0].id;
        setSelectedPlanId(initialId);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [service, selectedPlanId]);

  React.useEffect(() => { loadPlans(); }, []);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const features = React.useMemo(() => {
    if (!selectedPlan) return [];
    return [...selectedPlan.features].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [selectedPlan]);

  // Replace plan in local state after API returns updated plan
  const replacePlan = React.useCallback((updatedPlan: BillingPlanDto) => {
    setPlans((prev) => sortPlans(prev.map((p) => (p.id === updatedPlan.id ? updatedPlan : p))));
  }, []);

  // Open modal with form pre-populated (synchronous so TipTap mounts with content)
  const openCreateModal = (plan: BillingPlanDto) => {
    setForm({ ...emptyForm });
    setModalError(null);
    setFeatureModal({ mode: 'create', plan });
  };

  const openEditModal = (plan: BillingPlanDto, feature: BillingPlanFeatureDto) => {
    setForm({
      featureText: feature.text,
      isIncluded: feature.isIncluded,
      category: feature.category || '__none__',
      tooltipHtml: feature.tooltipHtml || '',
    });
    setModalError(null);
    setFeatureModal({ mode: 'edit', plan, feature });
  };

  const closeModal = () => {
    setFeatureModal(null);
    setForm(emptyForm);
    setModalError(null);
  };

  const handleSubmit = async () => {
    if (!featureModal) return;
    setSaving(true);
    setModalError(null);
    try {
      const category = form.category === '__none__' ? null : form.category;
      const tooltipHtml = form.tooltipHtml.trim() || null;

      if (featureModal.mode === 'create') {
        const maxOrder = featureModal.plan.features.reduce((max, f) => Math.max(max, f.displayOrder), -1);
        const input: AdminFeatureInput = {
          featureText: form.featureText.trim(),
          isIncluded: form.isIncluded,
          category,
          displayOrder: maxOrder + 1,
          tooltipHtml,
        };
        const updated = await service.createFeature(featureModal.plan.id, input);
        replacePlan(updated);
        useNotificationStore.getState().success('Feature created');
      } else {
        const input: AdminFeatureUpdateInput = {
          featureText: form.featureText.trim(),
          isIncluded: form.isIncluded,
          category,
          tooltipHtml,
        };
        const updated = await service.updateFeature(featureModal.feature.id, input);
        replacePlan(updated);
        useNotificationStore.getState().success('Feature updated');
      }
      closeModal();
    } catch (err: any) {
      setModalError(err?.message || 'Failed to save feature');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const updated = await service.deleteFeature(deleteConfirm.feature.id);
      replacePlan(updated);
      useNotificationStore.getState().success('Feature deleted');
      setDeleteConfirm(null);
    } catch (err: any) {
      useNotificationStore.getState().error(err?.message || 'Failed to delete feature');
    } finally {
      setDeleting(false);
    }
  };

  // Drag-and-drop reorder — insertionIdx is where the item should land (0..length)
  const handleDrop = React.useCallback(async (fromIdx: number, insertionIdx: number) => {
    // Convert insertion index to final array position accounting for removal
    let toIdx = insertionIdx;
    if (fromIdx < insertionIdx) toIdx -= 1; // removing item shifts indices down
    if (fromIdx === toIdx || !selectedPlan) return;
    setDragIdx(null);
    setDropLinePos(null);

    // Reorder locally first for instant feedback
    const reordered = [...features];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    // Optimistic local update
    const updatedFeatures = reordered.map((f, i) => ({ ...f, displayOrder: i }));
    const optimisticPlan = { ...selectedPlan, features: updatedFeatures };
    replacePlan(optimisticPlan);

    // Persist to backend
    setReordering(true);
    try {
      let latestPlan = optimisticPlan;
      for (let i = 0; i < updatedFeatures.length; i++) {
        const f = updatedFeatures[i];
        if (f.displayOrder !== features.find((orig) => orig.id === f.id)?.displayOrder) {
          latestPlan = await service.updateFeature(f.id, { displayOrder: i });
        }
      }
      replacePlan(latestPlan);
    } catch (err: any) {
      useNotificationStore.getState().error('Failed to save order');
      loadPlans(); // revert
    } finally {
      setReordering(false);
    }
  }, [features, selectedPlan, service, replacePlan, loadPlans]);

  // Calculate insertion position from mouse Y relative to item
  const calcDropPos = React.useCallback((e: React.DragEvent, idx: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    return e.clientY < midY ? idx : idx + 1;
  }, []);

  const planOptions = plans.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.key})${p.isActive ? '' : ' — inactive'}`,
  }));

  return (
    <PageContainer className="space-y-8">
      <PageHeader
        title="Plan Features & Tooltips"
        description="Manage the feature list and tooltip content shown on each pricing plan."
      />

      {error && (
        <div className="rounded-md border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" label="Loading plans" />
        </div>
      ) : (
        <>
          {/* Plan selector */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="flex-1 max-w-sm">
                  <Select
                    label="Select plan"
                    value={selectedPlanId}
                    onValueChange={setSelectedPlanId}
                    options={planOptions}
                  />
                </div>
                {selectedPlan && (
                  <Button onClick={() => openCreateModal(selectedPlan)}>
                    Add feature
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Features list */}
          {selectedPlan && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedPlan.name} — {features.length} feature{features.length !== 1 ? 's' : ''}
                </CardTitle>
                <CardDescription>
                  Click Edit to modify feature text, tooltip HTML, or display settings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {features.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    No features configured for this plan.
                  </p>
                ) : (
                  <div>
                    {reordering && (
                      <div className="py-2 px-3 text-xs text-blue-600 bg-blue-50 rounded mb-1 flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Saving new order...
                      </div>
                    )}
                    {features.map((feature, idx) => (
                      <React.Fragment key={feature.id}>
                        {/* Drop indicator line — before this item */}
                        <div
                          className={`h-0.5 -my-px transition-colors rounded-full ${
                            dropLinePos === idx && dragIdx !== null && dragIdx !== idx && dragIdx !== idx - 1
                              ? 'bg-blue-500'
                              : 'bg-transparent'
                          }`}
                        />
                        <div
                          draggable
                          onDragStart={(e) => {
                            setDragIdx(idx);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setDropLinePos(calcDropPos(e, idx));
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const pos = calcDropPos(e, idx);
                            if (dragIdx !== null) handleDrop(dragIdx, pos);
                          }}
                          onDragEnd={() => { setDragIdx(null); setDropLinePos(null); }}
                          className={`py-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between border-b border-gray-100 last:border-b-0 cursor-grab active:cursor-grabbing select-none ${
                            dragIdx === idx ? 'opacity-30' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Drag handle */}
                            <span className="text-gray-300 hover:text-gray-500 shrink-0 transition-colors" title="Drag to reorder">
                              <GripVertical className="w-4 h-4" />
                            </span>

                            {/* Included indicator */}
                            {feature.isIncluded ? (
                              <Check className="w-5 h-5 text-green-600 shrink-0" />
                            ) : (
                              <X className="w-5 h-5 text-red-400 shrink-0" />
                            )}

                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {feature.text}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                {categoryBadge(feature.category)}
                                {feature.tooltipHtml ? (
                                  <Badge variant="secondary">Has tooltip</Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">No tooltip</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(selectedPlan, feature)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => setDeleteConfirm({ feature, plan: selectedPlan })}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                    {/* Drop indicator line — after last item */}
                    <div
                      className={`h-0.5 -my-px transition-colors rounded-full ${
                        dropLinePos === features.length && dragIdx !== null && dragIdx !== features.length - 1
                          ? 'bg-blue-500'
                          : 'bg-transparent'
                      }`}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Feature edit/create modal */}
      <Modal
        isOpen={Boolean(featureModal)}
        onClose={saving ? () => {} : closeModal}
        title={featureModal?.mode === 'create' ? 'Add feature' : 'Edit feature'}
        description="Configure feature text, category, and tooltip content."
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : featureModal?.mode === 'create' ? 'Create feature' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <Input
            label="Feature text"
            placeholder="e.g. All 13+ image models"
            value={form.featureText}
            onChange={(e) => setForm((prev) => ({ ...prev, featureText: e.target.value }))}
            required
          />

          <Select
            label="Category"
            value={form.category}
            onValueChange={(value) => setForm((prev) => ({ ...prev, category: value }))}
            options={CATEGORY_OPTIONS}
          />

          <div className="flex items-center gap-3">
            <Switch
              id="feature-included"
              checked={form.isIncluded}
              onChange={(e) => setForm((prev) => ({ ...prev, isIncluded: e.target.checked }))}
            />
            <label htmlFor="feature-included" className="text-sm text-gray-700">
              Feature is included in this plan
            </label>
          </div>

          {/* Tooltip HTML editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Tooltip content (HTML)
            </label>
            <TipTapEditor
              key={featureModal?.mode === 'edit' ? featureModal.feature.id : 'new'}
              content={form.tooltipHtml}
              onChange={(html) => {
                // TipTap returns <p></p> for empty — treat as empty
                const cleaned = html === '<p></p>' ? '' : html;
                setForm((prev) => ({ ...prev, tooltipHtml: cleaned }));
              }}
              placeholder="Enter tooltip content... (leave empty for no tooltip)"
              className="min-h-[120px]"
            />
          </div>

          {/* Live tooltip preview */}
          {form.tooltipHtml && form.tooltipHtml !== '<p></p>' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Tooltip preview (as shown in paywall)
              </label>
              <div className="rounded-xl p-4 text-xs leading-relaxed paywall-tooltip-preview">
                <div dangerouslySetInnerHTML={{ __html: form.tooltipHtml }} />
              </div>
              <style>{`
                .paywall-tooltip-preview {
                  background: rgba(29, 41, 57, 0.95);
                  border: 1px solid rgba(255, 255, 255, 0.15);
                  color: rgba(255, 255, 255, 0.6);
                  font-size: 12px;
                  line-height: 1.6;
                }
                .paywall-tooltip-preview strong {
                  color: rgba(255, 255, 255, 0.85);
                  font-weight: 600;
                }
                .paywall-tooltip-preview p {
                  margin: 0 0 8px 0;
                }
                .paywall-tooltip-preview p:last-child {
                  margin-bottom: 0;
                }
                .paywall-tooltip-preview a {
                  color: rgba(200, 212, 90, 0.8);
                  text-decoration: underline;
                }
              `}</style>
            </div>
          )}

          {modalError && <div className="text-sm text-error-600">{modalError}</div>}
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={Boolean(deleteConfirm)}
        onClose={deleting ? () => {} : () => setDeleteConfirm(null)}
        title="Delete feature"
        description={`Are you sure you want to delete "${deleteConfirm?.feature.text}"? This cannot be undone.`}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? 'Deleting...' : 'Delete feature'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          This will permanently remove the feature from the <strong>{deleteConfirm?.plan.name}</strong> plan.
        </p>
      </Modal>
    </PageContainer>
  );
}
